/**
 * Public, share-friendly opportunity card rendered at `/opp/{id}`.
 *
 * No auth required. Pulls only the safe public fields from
 * `/api/opportunities/{id}/public` and renders a beautiful, social-share-ready
 * view with reasoning + verdict + signup CTA. Updates document.title so
 * social-card previews (Twitter, Slack, Discord) show the actual problem.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Sparkle, TwitterLogo, LinkedinLogo, LinkSimple, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { LuminaLogo } from "./LandingPage";
import { getCompetitionVerdict } from "./lib/reasoning";
import { CompetitionVerdict } from "./components/CompetitionVerdict";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAIN_BUCKET_LABELS = {
  integration: "Integration", cost: "Cost & Pricing", video: "Video Gen",
  quality: "Output Quality", domain_niche: "Niche-Specific", hallucination: "Hallucination",
  voice: "Voice & Audio", image: "Image Gen", content: "Content", agent: "AI Agents",
  data: "Data & Training", security: "Security",
};
const PAIN_BUCKET_COLORS = {
  integration: "#8B5CF6", cost: "#F97316", video: "#3B82F6", quality: "#14B8A6",
  domain_niche: "#10B981", hallucination: "#EF4444", voice: "#EC4899",
  image: "#A855F7", content: "#F59E0B", agent: "#6366F1", data: "#0EA5E9", security: "#84CC16",
};

const SOURCE_META = {
  reddit:          { label: "Reddit",       color: "#FF4500" },
  hackernews:      { label: "Hacker News",  color: "#FF6600" },
  fiverr:          { label: "Fiverr",       color: "#1DBF73" },
  freelancer:      { label: "Freelancer",   color: "#29B2FE" },
  indiehackers:    { label: "IndieHackers", color: "#0E2439" },
  producthunt:     { label: "ProductHunt",  color: "#DA552F" },
  github_trending: { label: "GitHub",       color: "#24292E" },
};

function buildReasoning(opp) {
  const sb = opp.source_breakdown || {};
  const demand   = (sb.reddit || 0) + (sb.hackernews || 0) + (sb.indiehackers || 0);
  const paid     = (sb.fiverr || 0) + (sb.freelancer || 0) + (sb.producthunt || 0);
  const builders = (sb.github_trending || 0) + (sb.producthunt || 0);

  const lines = [];
  if (demand >= 20) lines.push({ icon: "💬", text: `${demand} public complaints across forums — repeated, validated demand (not a one-off rant).` });
  else if (demand > 0) lines.push({ icon: "💬", text: `${demand} early discussion${demand > 1 ? "s" : ""} surfaced — niche but the conversation is starting.` });

  if (paid >= 5) lines.push({ icon: "💰", text: `${paid} paid transactions found on Fiverr / Freelancer / ProductHunt — the market is already opening its wallet for adjacent solutions.` });
  else if (paid > 0) lines.push({ icon: "💰", text: `${paid} paid signal${paid > 1 ? "s" : ""} of validation — proof someone will pay, even if the volume is small.` });

  if (builders > 0) lines.push({ icon: "🛠️", text: `${builders} builder${builders > 1 ? "s are" : " is"} actively shipping in this space (GitHub Trending + ProductHunt launches) — the tech is feasible RIGHT NOW.` });

  if ((opp.cross_source_confidence || 0) >= 3) {
    lines.push({ icon: "🎯", text: `Validated across ${opp.cross_source_confidence} independent sources — drastically reduces "is this just one loud user?" risk.` });
  }

  const score = Math.round(opp.weighted_total || 0);
  let action = "", tone = "violet";
  if (score >= 85 || (paid >= 5 && demand >= 20)) {
    action = "Ship now — demand × paid validation × builder momentum all aligned. First-mover window is wide open.";
    tone = "emerald";
  } else if (score >= 70 || (paid >= 2 && demand >= 10)) {
    action = "Worth a 5-day MVP — signals strong enough to warrant a landing page + paid traffic test.";
    tone = "violet";
  } else if (demand >= 10) {
    action = "Validate further — demand is real but the monetization path is unclear. Talk to 5 of the complainers before building.";
    tone = "amber";
  } else {
    action = "Watch — too early or too thin right now. Re-check the next scan cycle for momentum shifts.";
    tone = "zinc";
  }
  return { lines, action, tone, demand, paid, builders };
}

const TONE_STYLES = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", label: "🚀 Ship now" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-900",  label: "✅ Build the MVP" },
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   label: "🔬 Validate first" },
  zinc:    { bg: "bg-zinc-50",    border: "border-zinc-200",    text: "text-zinc-700",    label: "👀 Watch" },
};

export const OpportunityPublicView = ({ oppId, onEnterApp }) => {
  const [opp, setOpp] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOpp(null); setErr(null);
    axios.get(`${API}/opportunities/${oppId}/public`, { timeout: 10000 })
      .then(r => { if (!cancelled) setOpp(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.status === 404 ? "not_found" : "error"); });
    return () => { cancelled = true; };
  }, [oppId]);

  useEffect(() => {
    if (opp?.problem) {
      document.title = `${opp.problem.slice(0, 80)}${opp.problem.length > 80 ? "…" : ""} · Prime Pillar`;
      // Update meta description for SEO + social previews
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
      const score = Math.round(opp.weighted_total || 0);
      meta.content = `P2P Score ${score}/100 — ${opp.verdict || ""}. ${opp.problem.slice(0, 140)}`;
      // og:title + og:description
      const setOg = (prop, content) => {
        let el = document.querySelector(`meta[property="${prop}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
        el.content = content;
      };
      setOg("og:title", `${opp.verdict || "AI Opportunity"} · P2P ${score}/100`);
      setOg("og:description", opp.problem.slice(0, 200));
      setOg("og:type", "article");
      setOg("og:url", window.location.href);
      setOg("og:image", `${API}/opportunities/${opp.id}/og.png`);
      setOg("og:image:width", "1200");
      setOg("og:image:height", "630");
      // Twitter card (separate spec)
      const setName = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
        el.content = content;
      };
      setName("twitter:card", "summary_large_image");
      setName("twitter:title", `${opp.verdict || "AI Opportunity"} · P2P ${score}/100`);
      setName("twitter:description", opp.problem.slice(0, 200));
      setName("twitter:image", `${API}/opportunities/${opp.id}/og.png`);
    }
    return () => { document.title = "Prime Pillar"; };
  }, [opp]);

  if (err === "not_found") {
    return (
      <PublicShell onEnterApp={onEnterApp}>
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Opportunity not available</h1>
          <p className="text-zinc-500 mb-6">This opportunity may have been killed, marked private, or never existed.</p>
          <button
            onClick={onEnterApp}
            data-testid="public-opp-explore-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-all"
            style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}
          >
            Explore live opportunities <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </PublicShell>
    );
  }

  if (!opp) {
    return (
      <PublicShell onEnterApp={onEnterApp}>
        <div className="animate-pulse space-y-4 max-w-3xl mx-auto py-12">
          <div className="h-4 w-32 bg-zinc-200 rounded" />
          <div className="h-10 w-3/4 bg-zinc-200 rounded" />
          <div className="h-6 w-full bg-zinc-100 rounded" />
          <div className="h-32 w-full bg-zinc-100 rounded-2xl" />
        </div>
      </PublicShell>
    );
  }

  const score = Math.round(opp.weighted_total || 0);
  const verdict = opp.verdict || "PROMISING";
  const verdictBg = score >= 85 ? "bg-emerald-500" : score >= 70 ? "bg-amber-500" : score >= 50 ? "bg-zinc-400" : "bg-zinc-300";
  const bucket = opp.pain_bucket ? { label: PAIN_BUCKET_LABELS[opp.pain_bucket] || opp.pain_bucket, color: PAIN_BUCKET_COLORS[opp.pain_bucket] || "#8B5CF6" } : null;
  const reasoning = buildReasoning(opp);
  const tone = TONE_STYLES[reasoning.tone];
  const sb = opp.source_breakdown || {};
  const sources = Object.entries(SOURCE_META)
    .map(([k, m]) => ({ k, ...m, v: sb[k] || 0 }))
    .filter(s => s.v > 0);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const tweetText = encodeURIComponent(`📊 P2P Score ${score}/100 — ${opp.problem.slice(0, 180)}\n\nFull reasoning →`);
  const twitterHref = `https://twitter.com/intent/tweet?text=${tweetText}&url=${encodeURIComponent(shareUrl)}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Clipboard API can fail under non-HTTPS contexts or denied perms.
      // Surface in console so we know if it ever stops working.
      console.warn("Clipboard copy failed:", e);
    }
  };

  return (
    <PublicShell onEnterApp={onEnterApp}>
      <article className="max-w-3xl mx-auto" data-testid="public-opp-view">
        <button
          onClick={onEnterApp}
          className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-violet-700 mb-4 transition-colors"
          data-testid="public-opp-back"
        >
          <ArrowLeft size={12} weight="bold" /> Back to live signal feed
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] uppercase tracking-[0.18em] font-bold">
            <Sparkle size={10} weight="fill" /> {opp.ai_category || "AI Opportunity"}
          </div>
          <div className={`inline-flex items-center px-2.5 py-1 rounded-full ${verdictBg} text-white text-[10px] uppercase tracking-[0.18em] font-bold`}>
            {verdict}
          </div>
          {bucket && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-zinc-100 text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-700">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: bucket.color }} />
              {bucket.label}
            </div>
          )}
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 leading-tight mb-3" data-testid="public-opp-title">
          {opp.problem}
        </h1>
        {opp.user_type && (
          <p className="text-sm text-zinc-500 mb-6">Target user: <span className="font-medium text-zinc-700">{opp.user_type}</span></p>
        )}

        {/* Score row */}
        <div className="flex items-baseline gap-3 mb-8 pb-6 border-b border-zinc-200">
          <span className="text-6xl sm:text-7xl font-bold text-zinc-900 tabular-nums tracking-tight">{score}</span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">P2P Score</div>
            <div className="text-xs text-zinc-700">out of 100 · {opp.signal_strength || "—"} signal</div>
          </div>
          <div className="ml-auto flex flex-wrap gap-1.5 justify-end">
            <a href={twitterHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 text-white text-xs font-semibold hover:bg-zinc-700 transition-colors" data-testid="share-twitter">
              <TwitterLogo size={13} weight="fill" /> Share
            </a>
            <a href={linkedinHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0A66C2] text-white text-xs font-semibold hover:bg-[#0857A6] transition-colors" data-testid="share-linkedin">
              <LinkedinLogo size={13} weight="fill" /> Post
            </a>
            <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 text-xs font-semibold hover:bg-zinc-200 transition-colors" data-testid="share-copy">
              {copied ? <><CheckCircle size={13} weight="fill" /> Copied</> : <><LinkSimple size={13} weight="bold" /> Copy link</>}
            </button>
          </div>
        </div>

        {/* WHY THIS MATTERS */}
        {reasoning.lines.length > 0 && (
          <section className="mb-6" data-testid="public-opp-why">
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-violet-700 font-bold mb-3">Why this opportunity is worth your attention</h2>
            <ul className="space-y-2.5">
              {reasoning.lines.map((line, i) => (
                <li key={i} className="flex gap-3 text-sm text-zinc-800 leading-relaxed">
                  <span className="flex-shrink-0 text-base">{line.icon}</span>
                  <span>{line.text}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* OUR TAKE — overridden by the data-grounded Competition verdict when present */}
        {getCompetitionVerdict(opp) ? (
          <section className="mb-6" data-testid="public-opp-take">
            <CompetitionVerdict opportunity={opp} />
          </section>
        ) : (
          <section className={`p-4 rounded-xl border ${tone.bg} ${tone.border} ${tone.text} mb-6`} data-testid="public-opp-take">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-70 mb-1">Our take · {tone.label}</div>
            <div className="text-sm font-semibold leading-snug">{reasoning.action}</div>
          </section>
        )}

        {/* The gap (LLM-extracted) */}
        {opp.gap && opp.gap.length > 12 && !opp.gap.startsWith("Keywords:") && (
          <section className="mb-6">
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">Where the market gap is</h2>
            <p className="text-sm text-zinc-800 leading-relaxed">{opp.gap}</p>
          </section>
        )}

        {/* Automation potential */}
        {opp.automation_potential && opp.automation_potential.length > 20 && (
          <section className="mb-6">
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-2">How automatable is this?</h2>
            <p className="text-sm text-zinc-800 leading-relaxed">{opp.automation_potential}</p>
          </section>
        )}

        {/* Evidence sources */}
        {sources.length > 0 && (
          <section className="mb-8">
            <h2 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3">Evidence sources</h2>
            <div className="flex flex-wrap gap-2">
              {sources.map(s => (
                <span key={s.k} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-zinc-200 text-[11px] font-semibold text-zinc-700">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                  {s.label} <span className="tabular-nums text-zinc-500">{s.v}</span>
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Score breakdown */}
        <section className="mb-10 bg-zinc-50 rounded-2xl p-5 border border-zinc-100">
          <h2 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3">P2P Score breakdown</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { k: "demand_score", l: "Demand" },
              { k: "competition_score", l: "Competition" },
              { k: "automation_score", l: "Automation" },
              { k: "revenue_score", l: "Revenue" },
              { k: "speed_score", l: "Speed to MVP" },
            ].map(s => (
              <div key={s.k}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{s.l}</div>
                <div className="text-xl font-bold text-zinc-900 tabular-nums">{Math.round(opp[s.k] || 0)}<span className="text-xs text-zinc-400 font-normal">/100</span></div>
              </div>
            ))}
          </div>
        </section>

        {/* Conversion CTA */}
        <section className="rounded-2xl p-7 sm:p-9 text-center text-white shadow-xl mb-8" style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }} data-testid="public-opp-cta">
          <div className="text-xs uppercase tracking-[0.22em] font-bold opacity-80 mb-2">Want 45 more like this?</div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 tracking-tight">The full live feed updates 3× daily</h2>
          <p className="text-sm sm:text-base opacity-90 max-w-xl mx-auto mb-5">
            Prime Pillar scans 7 sources every 6 hours, scores every signal, and surfaces opportunities like this one with a clear ship/validate/watch verdict — so you stop guessing and start shipping.
          </p>
          <button
            onClick={onEnterApp}
            data-testid="public-opp-signup"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-violet-700 font-bold text-sm hover:scale-105 transition-transform shadow-lg"
          >
            Open the live dashboard <ArrowRight size={15} weight="bold" />
          </button>
          <div className="text-[11px] opacity-70 mt-3">Free to explore · No credit card required</div>
        </section>
      </article>
    </PublicShell>
  );
};

const PublicShell = ({ children, onEnterApp }) => (
  <div className="min-h-screen bg-[#F1F3F8]">
    <header className="px-5 sm:px-8 py-4 flex items-center justify-between max-w-7xl mx-auto">
      <button onClick={onEnterApp} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="public-shell-logo">
        <LuminaLogo size={36} />
        <span className="font-semibold text-zinc-900">Prime Pillar</span>
      </button>
      <button
        onClick={onEnterApp}
        data-testid="public-shell-cta"
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white font-semibold text-sm shadow-md hover:-translate-y-0.5 transition-all"
        style={{ background: "linear-gradient(115deg, #5E73C1 0%, #8B5CF6 60%, #A855F7 100%)" }}
      >
        Enter app <ArrowRight size={13} weight="bold" />
      </button>
    </header>
    <main className="px-5 sm:px-8 pb-16">{children}</main>
  </div>
);

export default OpportunityPublicView;
